// =============================================================================
// ACTIVE CALL VIEW
// =============================================================================
// Full-screen overlay shown during an active voice/video call

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useCallStore } from '../../stores/callStore.js';
import { useMemorySessionStore } from '../../stores/memorySessionStore.js';
import { useRemoteMedia } from '../../hooks/useRemoteMedia.js';
import { MemorySessionView, MemoryAlbumSelector } from '../memory/index.js';
import { ConnectionBanner, ConnectionQualityBars } from './ConnectionIndicator.js';
import { ReactionPicker } from '../chat/ReactionPicker.js';
import { CallReactionBurst } from './CallReactionBurst.js';
import {
  Images,
  Mic,
  MicOff,
  Video,
  VideoOff,
  PhoneOff,
  Volume2,
  VolumeX,
  RefreshCw,
  Smile,
  Sparkles,
} from '../ui/Icons.js';

// =============================================================================
// COMPONENT
// =============================================================================

// One call control: a circular icon with a visible word under it (iOS
// call-screen pattern, design brief section 5.3). The label sits inside the
// button so the whole column is the touch target.
function CallControl({
  label,
  ariaLabel,
  onClick,
  disabled,
  circleClass,
  size = 'md',
  children,
}: {
  label: string;
  ariaLabel?: string;
  onClick: () => void;
  disabled?: boolean;
  circleClass: string;
  size?: 'md' | 'lg';
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel ?? label}
      className="flex min-w-touch flex-col items-center gap-1.5 disabled:opacity-50"
    >
      <span
        className={`flex items-center justify-center rounded-full transition-all ${
          size === 'lg' ? 'h-16 w-16' : 'h-14 w-14'
        } ${circleClass}`}
      >
        {children}
      </span>
      <span className="text-xs font-medium text-ink-2">{label}</span>
    </button>
  );
}

export function ActiveCallView() {
  const { t } = useTranslation();
  const {
    activeCall,
    connectionState,
    reactions,
    hangUp,
    toggleVideo,
    toggleAudio,
    toggleSpeaker,
    flipCamera,
    toggleBackgroundBlur,
    sendReaction,
    dismissReaction,
    isLoading,
  } = useCallStore();

  // Memory session state
  const {
    session: memorySession,
    setupMemorySocketListeners,
    startDateMemorySession,
    pendingLaunch,
    clearPendingLaunch,
    error: memoryError,
    clearError: clearMemoryError,
  } = useMemorySessionStore();

  // Ref for the local self-view video element
  const localVideoRef = useRef<HTMLVideoElement>(null);

  // Bind the remote peer's stream to whichever media element is mounted (the
  // full-screen <video> for video calls, the always-mounted <audio> sink for
  // voice). One sink per stream - the W2 grid calls this hook once per peer.
  const remoteStream = activeCall?.remoteStreams.values().next().value;
  const setRemoteMedia = useRemoteMedia(remoteStream, {
    sinkId: activeCall?.speakerSinkId,
  });

  // Call duration timer
  const [duration, setDuration] = useState(0);

  // Album selector modal state
  const [showAlbumSelector, setShowAlbumSelector] = useState(false);

  // In-call reaction picker visibility (tap-to-react)
  const [showReactionPicker, setShowReactionPicker] = useState(false);

  // Setup memory session socket listeners
  useEffect(() => {
    const cleanup = setupMemorySocketListeners();
    return cleanup;
  }, [setupMemorySocketListeners]);

  // A memory tapped from the chat strip (or the daily prompt) requested a
  // co-view. Once this call is active, start the date-clustered smart memory
  // directly - the server assembles the photo set from that day's shared
  // photos, so no album choice is needed. The "Share photos" button still
  // opens the album picker for an explicit album co-view.
  useEffect(() => {
    if (pendingLaunch && activeCall?.status === 'active') {
      const { conversationId, date } = pendingLaunch;
      clearPendingLaunch();
      void startDateMemorySession(activeCall.id, conversationId, date);
    }
  }, [pendingLaunch, activeCall?.status, activeCall?.id, clearPendingLaunch, startDateMemorySession]);

  // Attach local stream to video element
  useEffect(() => {
    if (activeCall?.localStream && localVideoRef.current) {
      localVideoRef.current.srcObject = activeCall.localStream;
    }
  }, [activeCall?.localStream]);

  // Duration timer
  useEffect(() => {
    if (activeCall?.status === 'active' && activeCall?.startedAt) {
      const startTime = new Date(activeCall.startedAt).getTime();

      const timer = setInterval(() => {
        const now = Date.now();
        const elapsed = Math.floor((now - startTime) / 1000);
        setDuration(elapsed);
      }, 1000);

      return () => { clearInterval(timer); };
    }
  }, [activeCall?.status, activeCall?.startedAt]);

  if (!activeCall) {
    return null;
  }

  // If memory session is active, show the split-screen view
  if (memorySession) {
    return <MemorySessionView />;
  }

  const isVideoCall = activeCall.callType === 'video';
  const isConnecting = activeCall.status === 'initiating' || activeCall.status === 'connecting' || activeCall.status === 'ringing';
  const hasRemoteStream = activeCall.remoteStreams.size > 0;
  const isCallActive = activeCall.status === 'active';

  // Get remote participant info (first non-self participant)
  const remoteParticipant = activeCall.participants[0];

  // The remote mute state flows in over call:participant-updated. Participants
  // are seeded audio-on, so this is true only once the peer has actually muted.
  const isRemoteMuted = remoteParticipant != null && !remoteParticipant.isAudioEnabled;
  const remoteName = remoteParticipant?.displayName || t('chat.unknown', 'Unknown');

  // Format duration as MM:SS
  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleHangUp = async () => {
    await hangUp();
  };

  const handleReactionSelect = (emoji: string) => {
    sendReaction(emoji);
    setShowReactionPicker(false);
  };

  // A co-view started from the chat strip / daily prompt (not the picker) can
  // come back empty. The picker is not open in that case, so surface a
  // dismissible banner here rather than leaving the bare call screen as a dead
  // end. Picker-initiated failures are shown inside the picker instead.
  const memoryEmptyBanner =
    !showAlbumSelector && memoryError === 'memory:empty:date'
      ? t('memory.emptyDate', 'No photos were shared on this day in earlier years yet.')
      : !showAlbumSelector && memoryError === 'memory:empty:recap'
        ? t('memory.emptyRecap', 'There are no shared photos to recap yet. Share some photos in this chat, then try again.')
        : null;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-surface-1">
      {memoryEmptyBanner && (
        <div
          className="absolute left-1/2 top-4 z-30 flex max-w-[90%] -translate-x-1/2 items-center gap-3 rounded-xl bg-surface-2 px-4 py-3 text-sm text-ink-1 shadow-lg"
          role="status"
        >
          <span>{memoryEmptyBanner}</span>
          <button
            onClick={clearMemoryError}
            className="min-h-touch shrink-0 rounded-lg bg-primary px-3 py-1.5 font-medium text-primary-on transition-colors hover:bg-primary-hover"
          >
            {t('common.ok', 'OK')}
          </button>
        </div>
      )}
      {/* Main video/audio area */}
      <div className="relative flex-1">
        {/* Ephemeral in-call reaction bursts (own taps + relayed peer taps) */}
        <CallReactionBurst reactions={reactions} onDone={dismissReaction} />
        {isVideoCall ? (
          <>
            {/* Remote video (full screen) */}
            <div className="absolute inset-0 bg-surface-2">
              {hasRemoteStream ? (
                <video
                  ref={setRemoteMedia}
                  autoPlay
                  playsInline
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex h-full w-full flex-col items-center justify-center">
                  {/* Avatar placeholder when no remote video */}
                  <div className="mb-4 flex h-32 w-32 items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary-press text-5xl font-bold text-ink-1 shadow-xl">
                    {remoteParticipant?.displayName?.charAt(0).toUpperCase() || '?'}
                  </div>
                  <p className="text-xl font-medium text-ink-1">
                    {remoteParticipant?.displayName || 'Unknown'}
                  </p>
                </div>
              )}
            </div>

            {/* Local video (PiP - top right) */}
            <div className="absolute right-4 top-4 h-36 w-28 overflow-hidden rounded-xl bg-surface-3 shadow-lg">
              {activeCall.isLocalVideoEnabled && activeCall.localStream ? (
                <video
                  ref={localVideoRef}
                  autoPlay
                  playsInline
                  muted
                  className="h-full w-full object-cover"
                  style={{ transform: 'scaleX(-1)' }}
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center bg-surface-3">
                  <span className="text-2xl">📷</span>
                  <span className="absolute bottom-1 text-xs text-ink-3">
                    {t('calls.videoOff', 'Off')}
                  </span>
                </div>
              )}
            </div>

            {/* Remote name overlay + muted badge (video) */}
            <div className="absolute bottom-4 left-4 flex items-center gap-2 rounded-full bg-surface-1/70 px-3 py-1.5 backdrop-blur-sm">
              <span className="max-w-[12rem] truncate text-sm font-medium text-ink-1">
                {remoteName}
              </span>
              {isRemoteMuted && (
                <span
                  className="flex items-center gap-1 text-danger"
                  aria-label={t('calls.participantMuted', '{{name}} is muted', { name: remoteName })}
                  title={t('calls.participantMuted', '{{name}} is muted', { name: remoteName })}
                >
                  <MicOff className="h-4 w-4" />
                </span>
              )}
            </div>
          </>
        ) : (
          /* Voice call - centered avatar */
          <div className="flex h-full w-full flex-col items-center justify-center bg-gradient-to-b from-surface-2 to-surface-1">
            {/*
              Remote audio sink. Always mounted for voice calls so the negotiated
              remote audio track actually plays - this is the element whose
              absence made voice calls silently inaudible. Self-mic is never
              played here; only the remote stream is bound.
            */}
            <audio ref={setRemoteMedia} autoPlay />

            {/* Remote avatar */}
            <div className="mb-6 flex h-40 w-40 items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary-press text-6xl font-bold text-ink-1 shadow-2xl">
              {remoteParticipant?.displayName?.charAt(0).toUpperCase() || '?'}
            </div>

            {/* Remote name */}
            <h2 className="mb-2 text-2xl font-bold text-ink-1">
              {remoteName}
            </h2>

            {/* Muted badge */}
            {isRemoteMuted && (
              <div className="mb-2 flex items-center gap-2 rounded-full bg-danger-soft px-3 py-1 text-danger">
                <MicOff className="h-4 w-4" />
                <span className="text-sm font-medium">
                  {t('calls.participantMuted', '{{name}} is muted', { name: remoteName })}
                </span>
              </div>
            )}

            {/* Speaking indicator */}
            {remoteParticipant?.isSpeaking && (
              <div className="mb-4 flex items-center gap-2 text-primary">
                <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-success" />
                <span>{t('calls.speaking', 'Speaking')}</span>
              </div>
            )}
          </div>
        )}

        {/* Status overlay */}
        <div className="absolute left-0 right-0 top-8 flex flex-col items-center">
          {/* Honest connection banner (reconnecting / lost / restored) */}
          <ConnectionBanner state={connectionState} />

          {/* Call status */}
          {isConnecting ? (
            <div className="rounded-full bg-surface-1/80 px-4 py-2 backdrop-blur-sm">
              <div className="flex items-center gap-2 text-ink-1">
                <div className="h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent" />
                <span>
                  {activeCall.status === 'initiating' && t('calls.initiating', 'Initiating...')}
                  {activeCall.status === 'ringing' && t('calls.ringing', 'Ringing...')}
                  {activeCall.status === 'connecting' && t('calls.connecting', 'Connecting...')}
                </span>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 rounded-full bg-surface-1/80 px-4 py-2 backdrop-blur-sm">
              <ConnectionQualityBars state={connectionState} callStatus={activeCall.status} />
              <span className="font-mono text-lg text-ink-1">
                {formatDuration(duration)}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Control bar: every circular control carries a visible word under the
          icon (design brief section 5.3). Wraps on narrow screens so the full
          video-call set never overflows at 393px. */}
      <div className="flex flex-wrap items-start justify-center gap-3 sm:gap-6 bg-surface-2 px-4 py-5 safe-area-pb">
        {/* Mute button */}
        <CallControl
          label={
            activeCall.isLocalAudioEnabled
              ? t('calls.mute', 'Mute')
              : t('calls.unmute', 'Unmute')
          }
          onClick={toggleAudio}
          circleClass={
            activeCall.isLocalAudioEnabled
              ? 'bg-surface-3 text-ink-1 hover:bg-surface-sunken'
              : 'bg-danger text-white hover:bg-rowan-700'
          }
        >
          {activeCall.isLocalAudioEnabled
            ? <Mic className="h-6 w-6" />
            : <MicOff className="h-6 w-6" />}
        </CallControl>

        {/* Video toggle (only for video calls) */}
        {isVideoCall && (
          <CallControl
            label={t('calls.camera', 'Camera')}
            ariaLabel={
              activeCall.isLocalVideoEnabled
                ? t('calls.cameraOff', 'Turn off camera')
                : t('calls.cameraOn', 'Turn on camera')
            }
            onClick={toggleVideo}
            circleClass={
              activeCall.isLocalVideoEnabled
                ? 'bg-surface-3 text-ink-1 hover:bg-surface-sunken'
                : 'bg-danger text-white hover:bg-rowan-700'
            }
          >
            {activeCall.isLocalVideoEnabled
              ? <Video className="h-6 w-6" />
              : <VideoOff className="h-6 w-6" />}
          </CallControl>
        )}

        {/* Tap-to-react (only when call is active) */}
        {isCallActive && (
          <div className="relative">
            {showReactionPicker && (
              <div className="absolute bottom-full left-1/2 mb-3 -translate-x-1/2">
                <ReactionPicker
                  onSelect={handleReactionSelect}
                  onClose={() => { setShowReactionPicker(false); }}
                />
              </div>
            )}
            <CallControl
              label={t('calls.reactLabel', 'React')}
              ariaLabel={t('calls.react', 'Send a reaction')}
              onClick={() => { setShowReactionPicker((v) => !v); }}
              circleClass={
                showReactionPicker
                  ? 'bg-primary text-primary-on'
                  : 'bg-surface-3 text-ink-1 hover:bg-surface-sunken'
              }
            >
              <Smile className="h-6 w-6" />
            </CallControl>
          </div>
        )}

        {/* Memory Session / Share Photos button (only when call is active) */}
        {isCallActive && (
          <CallControl
            label={t('nav.photos', 'Photos')}
            ariaLabel={t('memory.sharePhotos', 'Share photos')}
            onClick={() => { setShowAlbumSelector(true); }}
            circleClass="bg-primary text-primary-on hover:bg-primary-hover"
          >
            <Images className="h-6 w-6" />
          </CallControl>
        )}

        {/* End call button */}
        <CallControl
          label={t('calls.end', 'End')}
          ariaLabel={t('calls.endCall', 'End call')}
          onClick={() => { void handleHangUp(); }}
          disabled={isLoading}
          size="lg"
          circleClass="bg-danger text-white shadow-lg hover:bg-rowan-700"
        >
          <PhoneOff className="h-7 w-7" />
        </CallControl>

        {/* Speaker toggle */}
        <CallControl
          label={t('calls.speaker', 'Speaker')}
          ariaLabel={
            activeCall.isSpeakerEnabled
              ? t('calls.speakerOff', 'Turn off speaker')
              : t('calls.speakerOn', 'Turn on speaker')
          }
          onClick={toggleSpeaker}
          circleClass={
            activeCall.isSpeakerEnabled
              ? 'bg-surface-3 text-ink-1 hover:bg-surface-sunken'
              : 'bg-surface-3 text-ink-3 hover:bg-surface-sunken'
          }
        >
          {activeCall.isSpeakerEnabled
            ? <Volume2 className="h-6 w-6" />
            : <VolumeX className="h-6 w-6" />}
        </CallControl>

        {/* Flip camera (only for video calls) */}
        {isVideoCall && (
          <CallControl
            label={t('calls.flip', 'Flip')}
            ariaLabel={t('calls.flipCamera', 'Flip camera')}
            onClick={flipCamera}
            disabled={!activeCall.isLocalVideoEnabled}
            circleClass="bg-surface-3 text-ink-1 hover:bg-surface-sunken"
          >
            <RefreshCw className="h-6 w-6" />
          </CallControl>
        )}

        {/* Background blur toggle (only for video calls) */}
        {isVideoCall && (
          <CallControl
            label={t('calls.blur', 'Blur')}
            ariaLabel={
              activeCall.isBackgroundBlurred
                ? t('calls.blurOff', 'Turn off background blur')
                : t('calls.blurOn', 'Blur background')
            }
            onClick={toggleBackgroundBlur}
            disabled={!activeCall.isLocalVideoEnabled}
            circleClass={
              activeCall.isBackgroundBlurred
                ? 'bg-primary text-primary-on'
                : 'bg-surface-3 text-ink-1 hover:bg-surface-sunken'
            }
          >
            <Sparkles className="h-6 w-6" />
          </CallControl>
        )}
      </div>

      {/* Album selector modal */}
      {showAlbumSelector && (
        <MemoryAlbumSelector
          callId={activeCall.id}
          conversationId={activeCall.conversationId}
          onClose={() => { setShowAlbumSelector(false); }}
        />
      )}

      {/* CSS for safe area */}
      <style>{`
        .safe-area-pb {
          padding-bottom: max(1.5rem, env(safe-area-inset-bottom));
        }
      `}</style>
    </div>
  );
}
